export type CanvasRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export type Guide = {
  axis: "x" | "y";
  position: number;
};

export type SnapOptions = {
  canvasWidth: number;
  canvasHeight: number;
  siblings: CanvasRect[];
  gridSize: number;
  snapEnabled: boolean;
  thresholdCanvasPx: number;
};

function clamp(value: number, min: number, max: number): number {
  if (max < min) {
    return min;
  }

  return Math.min(Math.max(value, min), max);
}

function buildAxisTargets(
  canvasSize: number,
  siblingEdges: number[],
): number[] {
  return [0, canvasSize, canvasSize / 2, ...siblingEdges];
}

function edgesForAxis(rects: CanvasRect[], axis: "x" | "y"): number[] {
  const edges: number[] = [];

  for (const rect of rects) {
    if (axis === "x") {
      edges.push(rect.x, rect.x + rect.width / 2, rect.x + rect.width);
    } else {
      edges.push(rect.y, rect.y + rect.height / 2, rect.y + rect.height);
    }
  }

  return edges;
}

type SnapResult = { value: number; guide?: number };

// Snaps a single moving edge (used for resize, where only one edge of the
// rect is in motion) against the grid and the provided targets.
function snapSingleValue(
  raw: number,
  targets: number[],
  gridSize: number,
  threshold: number,
  snapEnabled: boolean,
): SnapResult {
  if (!snapEnabled) {
    return { value: raw };
  }

  let best: { diff: number; target: number } | undefined;

  for (const target of targets) {
    const diff = target - raw;

    if (
      Math.abs(diff) <= threshold &&
      (!best || Math.abs(diff) < Math.abs(best.diff))
    ) {
      best = { diff, target };
    }
  }

  if (best) {
    return { value: raw + best.diff, guide: best.target };
  }

  if (gridSize > 0) {
    return { value: Math.round(raw / gridSize) * gridSize };
  }

  return { value: raw };
}

// Snaps a moving rect's position (used for move, where the rect's near
// edge, center, and far edge are all candidates for a single axis).
function snapRange(
  rawPos: number,
  size: number,
  targets: number[],
  gridSize: number,
  threshold: number,
  snapEnabled: boolean,
): SnapResult {
  if (!snapEnabled) {
    return { value: rawPos };
  }

  let best: { diff: number; target: number } | undefined;

  for (const offset of [0, size / 2, size]) {
    const edgeValue = rawPos + offset;

    for (const target of targets) {
      const diff = target - edgeValue;

      if (
        Math.abs(diff) <= threshold &&
        (!best || Math.abs(diff) < Math.abs(best.diff))
      ) {
        best = { diff, target };
      }
    }
  }

  if (best) {
    return { value: rawPos + best.diff, guide: best.target };
  }

  if (gridSize > 0) {
    return { value: Math.round(rawPos / gridSize) * gridSize };
  }

  return { value: rawPos };
}

export function applyMove(
  startRect: CanvasRect,
  dx: number,
  dy: number,
  options: SnapOptions,
): { rect: CanvasRect; guides: Guide[] } {
  const targetsX = buildAxisTargets(
    options.canvasWidth,
    edgesForAxis(options.siblings, "x"),
  );
  const targetsY = buildAxisTargets(
    options.canvasHeight,
    edgesForAxis(options.siblings, "y"),
  );

  const snappedX = snapRange(
    startRect.x + dx,
    startRect.width,
    targetsX,
    options.gridSize,
    options.thresholdCanvasPx,
    options.snapEnabled,
  );
  const snappedY = snapRange(
    startRect.y + dy,
    startRect.height,
    targetsY,
    options.gridSize,
    options.thresholdCanvasPx,
    options.snapEnabled,
  );

  const maxX = Math.max(0, options.canvasWidth - startRect.width);
  const maxY = Math.max(0, options.canvasHeight - startRect.height);

  const x = clamp(snappedX.value, 0, maxX);
  const y = clamp(snappedY.value, 0, maxY);

  const guides: Guide[] = [];
  if (snappedX.guide !== undefined && x === snappedX.value) {
    guides.push({ axis: "x", position: snappedX.guide });
  }
  if (snappedY.guide !== undefined && y === snappedY.value) {
    guides.push({ axis: "y", position: snappedY.guide });
  }

  return {
    rect: {
      x: Math.round(x),
      y: Math.round(y),
      width: startRect.width,
      height: startRect.height,
    },
    guides,
  };
}

export function applyResize(
  startRect: CanvasRect,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  options: SnapOptions,
  minSize: number,
): { rect: CanvasRect; guides: Guide[] } {
  const guides: Guide[] = [];
  let { x, y, width, height } = startRect;

  const targetsX = buildAxisTargets(
    options.canvasWidth,
    edgesForAxis(options.siblings, "x"),
  );
  const targetsY = buildAxisTargets(
    options.canvasHeight,
    edgesForAxis(options.siblings, "y"),
  );

  if (handle.includes("w")) {
    const snapped = snapSingleValue(
      startRect.x + dx,
      targetsX,
      options.gridSize,
      options.thresholdCanvasPx,
      options.snapEnabled,
    );
    const maxLeft = startRect.x + startRect.width - minSize;
    const newLeft = clamp(snapped.value, 0, maxLeft);

    x = newLeft;
    width = startRect.x + startRect.width - newLeft;

    if (snapped.guide !== undefined && newLeft === snapped.value) {
      guides.push({ axis: "x", position: snapped.guide });
    }
  } else if (handle.includes("e")) {
    const snapped = snapSingleValue(
      startRect.x + startRect.width + dx,
      targetsX,
      options.gridSize,
      options.thresholdCanvasPx,
      options.snapEnabled,
    );
    const minRight = startRect.x + minSize;
    const newRight = clamp(snapped.value, minRight, options.canvasWidth);

    width = newRight - startRect.x;

    if (snapped.guide !== undefined && newRight === snapped.value) {
      guides.push({ axis: "x", position: snapped.guide });
    }
  }

  if (handle.includes("n")) {
    const snapped = snapSingleValue(
      startRect.y + dy,
      targetsY,
      options.gridSize,
      options.thresholdCanvasPx,
      options.snapEnabled,
    );
    const maxTop = startRect.y + startRect.height - minSize;
    const newTop = clamp(snapped.value, 0, maxTop);

    y = newTop;
    height = startRect.y + startRect.height - newTop;

    if (snapped.guide !== undefined && newTop === snapped.value) {
      guides.push({ axis: "y", position: snapped.guide });
    }
  } else if (handle.includes("s")) {
    const snapped = snapSingleValue(
      startRect.y + startRect.height + dy,
      targetsY,
      options.gridSize,
      options.thresholdCanvasPx,
      options.snapEnabled,
    );
    const minBottom = startRect.y + minSize;
    const newBottom = clamp(snapped.value, minBottom, options.canvasHeight);

    height = newBottom - startRect.y;

    if (snapped.guide !== undefined && newBottom === snapped.value) {
      guides.push({ axis: "y", position: snapped.guide });
    }
  }

  return {
    rect: {
      x: Math.round(x),
      y: Math.round(y),
      width: Math.round(width),
      height: Math.round(height),
    },
    guides,
  };
}

export function applyKeyboardMove(
  rect: CanvasRect,
  dx: number,
  dy: number,
  canvasWidth: number,
  canvasHeight: number,
): CanvasRect {
  const maxX = Math.max(0, canvasWidth - rect.width);
  const maxY = Math.max(0, canvasHeight - rect.height);

  return {
    ...rect,
    x: clamp(rect.x + dx, 0, maxX),
    y: clamp(rect.y + dy, 0, maxY),
  };
}

export function applyKeyboardResize(
  rect: CanvasRect,
  dw: number,
  dh: number,
  canvasWidth: number,
  canvasHeight: number,
  minSize: number,
): CanvasRect {
  const maxWidth = Math.max(minSize, canvasWidth - rect.x);
  const maxHeight = Math.max(minSize, canvasHeight - rect.y);

  return {
    ...rect,
    width: clamp(rect.width + dw, minSize, maxWidth),
    height: clamp(rect.height + dh, minSize, maxHeight),
  };
}

export function isRectOutOfBounds(
  rect: CanvasRect,
  canvasWidth: number,
  canvasHeight: number,
): boolean {
  return (
    rect.x < 0 ||
    rect.y < 0 ||
    rect.x + rect.width > canvasWidth ||
    rect.y + rect.height > canvasHeight
  );
}

export function fitRectToCanvas(
  rect: CanvasRect,
  canvasWidth: number,
  canvasHeight: number,
  minSize: number,
): CanvasRect {
  const width = clamp(rect.width, minSize, Math.max(minSize, canvasWidth));
  const height = clamp(rect.height, minSize, Math.max(minSize, canvasHeight));

  return {
    x: Math.round(clamp(rect.x, 0, Math.max(0, canvasWidth - width))),
    y: Math.round(clamp(rect.y, 0, Math.max(0, canvasHeight - height))),
    width: Math.round(width),
    height: Math.round(height),
  };
}
